import { Link, useParams, useSearchParams } from "react-router-dom";
import { Banner, Card, Page, Spinner, StatTile, StatTileGroup, UnderlineTabs } from "../../components";
import { useAuthorityGaps, useBatchRuns, usePractice, usePracticeClients } from "../../api/hooks";
import { formatDate, formatDateTime } from "../../api/types";
import { t } from "../../i18n/t";
import { AuthorityGapsTab } from "./AuthorityGapsTab";
import { BatchRunsTab } from "./BatchRunsTab";
import { ClientsTab } from "./ClientsTab";
import { MembersTab } from "./MembersTab";
import { RateBudgetTab } from "./RateBudgetTab";
import { roleLabel } from "./roleLabel";

const TABS = ["overview", "clients", "batch-runs", "members", "gaps", "budget"] as const;
type Tab = (typeof TABS)[number];

const TAB_LABELS: Record<Tab, () => string> = {
  overview: () => t("practice.detail.tab_overview"),
  clients: () => t("practice.detail.tab_clients"),
  "batch-runs": () => t("practice.detail.tab_batch_runs"),
  members: () => t("practice.detail.tab_members"),
  gaps: () => t("practice.detail.tab_gaps"),
  budget: () => t("practice.detail.tab_budget"),
};

/** `/practice/:practiceId` (P5-web §2b), redrawn as PracticeOverview.html: the breadcrumb, the 48 px initial
 * tile beside the h1 and the "N members · you are {role}" line, and the shared underline tab bar. Tabs are a
 * `?tab=` search param rather than nested routes, so every section is a shareable/bookmarkable link without
 * adding a second layer of route files under `screens/practice`. */
const TERMINAL_BATCH_STATES: ReadonlySet<string> = new Set(["COMPLETE", "COMPLETED", "FAILED", "CANCELLED"]);

export function PracticeDetailScreen(): JSX.Element {
  const { practiceId } = useParams();
  const id = practiceId ?? "";
  const practice = usePractice(id === "" ? null : id);
  const [params] = useSearchParams();
  const requested = params.get("tab");
  const tab: Tab = (TABS as readonly string[]).includes(requested ?? "") ? (requested as Tab) : "overview";

  if (practice.isPending) {
    return (
      <Page title={t("practice.detail.loading_title")}>
        <Spinner />
      </Page>
    );
  }
  if (practice.isError || practice.data === undefined) {
    return (
      <Page title={t("practice.detail.not_found")}>
        <Banner tone="block">{t("practice.detail.not_found")}</Banner>
      </Page>
    );
  }

  const detail = practice.data;
  const ownerView = detail.role === "OWNER";

  return (
    <Page
      breadcrumb={
        <nav className="breadcrumb" aria-label={t("a11y.breadcrumb")}>
          <Link to="/practice">{t("practice.landing.title")}</Link> <span aria-hidden="true">/</span>{" "}
          {detail.name}
        </nav>
      }
      titlePrefix={
        <span className="prtile prtile--48" aria-hidden="true">
          {detail.name.slice(0, 1).toUpperCase() || "P"}
        </span>
      }
      title={detail.name}
      caption={t("practice.detail.subtitle", {
        count: detail.member_count,
        role: roleLabel(detail.role),
      })}
      headerBottom={
        <UnderlineTabs
          className="tabs--ptabs"
          label={t("practice.detail.tabs_label")}
          currentKey={tab}
          tabs={[
            ...TABS.map((value) => ({
              key: value as string,
              label: TAB_LABELS[value](),
              href: value === "overview" ? `/practice/${id}` : `/practice/${id}?tab=${value}`,
            })),
            // The Settings "tab" is the anchor at the bottom of the Overview page (PracticeOverview.html),
            // never a current one.
            { key: "settings", label: t("practice.detail.tab_settings"), href: `/practice/${id}#ps` },
          ]}
        />
      }
    >
      {tab === "overview" ? <Overview practiceId={id} memberCount={detail.member_count} /> : null}
      {tab === "clients" ? <ClientsTab practiceId={id} /> : null}
      {tab === "batch-runs" ? <BatchRunsTab practiceId={id} /> : null}
      {tab === "members" ? <MembersTab practiceId={id} ownerView={ownerView} /> : null}
      {tab === "gaps" ? <AuthorityGapsTab practiceId={id} /> : null}
      {tab === "budget" ? <RateBudgetTab practiceId={id} /> : null}
    </Page>
  );
}

/** The settings card, shown both as the anchor at the bottom of the Overview page (PracticeOverview.html,
 * `#ps`) and on its own `?tab=settings`. */
function SettingsSection({ practiceId }: { practiceId: string }): JSX.Element {
  const practice = usePractice(practiceId);
  const detail = practice.data;
  if (detail === undefined) return <Spinner />;
  return (
    <section className="card" id="ps" aria-labelledby="practice-settings-title">
      <h2 className="card__title" id="practice-settings-title">
        {t("practice.detail.tab_settings")}
      </h2>
      <dl className="meta3">
        <div>
          <dt className="eyebrow">{t("practice.settings.lucibook_id")}</dt>
          <dd className="mono">{detail.lucibook_practice_id}</dd>
        </div>
        <div>
          <dt className="eyebrow">{t("practice.settings.created_at")}</dt>
          <dd className="mono">{formatDate(detail.created_at)}</dd>
        </div>
        <div>
          <dt className="eyebrow">{t("practice.settings.synced_at")}</dt>
          <dd className="mono">
            {detail.synced_at === null || detail.synced_at === undefined
              ? t("practice.settings.never_synced")
              : formatDateTime(detail.synced_at)}
          </dd>
        </div>
      </dl>
    </section>
  );
}

function Overview({ practiceId, memberCount }: { practiceId: string; memberCount: number }): JSX.Element {
  const clients = usePracticeClients(practiceId, null);
  const gaps = useAuthorityGaps(practiceId);
  // Fable #14: the count used to come from `listSessionBatchRunIds` (sessionStorage, browser-local, per
  // tab). It now comes from the same `GET /practices/{practice_id}/batch-runs` page `BatchRunsTab` reads,
  // so this number matches what that tab actually shows rather than what this one browser once created.
  const batchRuns = useBatchRuns(practiceId);

  return (
    <>
      {/* D10: today's data source — `member_count` and the item count of the FIRST page of clients, batch
          runs and gaps, exactly as before. The batch tile is labelled ACTIVE (PracticeOverview.html's
          "2" alongside three rows, one of them Complete): terminal rows never count. */}
      <StatTileGroup label={t("practice.overview.summary_label")}>
        <StatTile label={t("practice.overview.members")} value={memberCount} />
        <StatTile label={t("practice.overview.clients")} value={clients.data?.items.length ?? 0} />
        <StatTile
          label={t("practice.overview.active_batch_runs")}
          value={(batchRuns.data?.items ?? []).filter((row) => !TERMINAL_BATCH_STATES.has(row.status)).length}
        />
        <StatTile
          label={t("practice.overview.pending_gaps")}
          value={gaps.data?.items.length ?? 0}
          tone="fail"
          footer={<Link to={`/practice/${practiceId}?tab=gaps`}>{t("practice.overview.review_gaps")}</Link>}
        />
      </StatTileGroup>
      <Card
        flush
        title={t("practice.detail.tab_batch_runs")}
        caption={t("practice.overview.batch_runs_note")}
      >
        <BatchRunsTab practiceId={practiceId} bare />
      </Card>
      <SettingsSection practiceId={practiceId} />
    </>
  );
}
